import {Feature, FeatureWithDistance, LngLat} from "./DistributorsMap.types";
import {isStringContainSpace, frequencySort, distanceKm, deg2rad, rad2deg} from "./helper";

const maximumDealerDistance = 25;

export function filterAndSortDealerListByDistance(center: LngLat, dealerList: Feature[]) {
  const box = new BoundingBox(center, maximumDealerDistance);
  // performance optimization: we first filter for a rough (rectangular) bounding box, then calculate "exact" distance
  // and then filter all outside our desired maximum distance (circle around the map center)
  const dealersInBox: FeatureWithDistance[] = dealerList
    .filter((feature) => box.isWithin(feature.geometry.coordinates))
    .map((feature): FeatureWithDistance => [
      distanceKm(center, feature.geometry.coordinates),
      feature
    ])
    .filter(([distance, feature]) => distance <= maximumDealerDistance)
    .sort((left: [number, Feature], right: [number, Feature]) => left[0] - right[0]);

  return dealersInBox;
}

/**
 * An (approximate) bounding box defined by a center and a radius. The shape of the box is approximately a square with
 * (2 * diameter).
 */
export class BoundingBox {
  public readonly topLat: number;
  public readonly bottomLat: number;

  public readonly leftLng: number;
  public readonly rightLng: number;

  /**
   *
   * @param center Center coordinate of the box
   * @param diameter Approximate diameter of the box in km.
   */
  constructor(public readonly center: LngLat, readonly diameter: number = 50) {

    const R = 6371; // earth radius in km
    const radiusOnLatitude = Math.cos(deg2rad(center[1])) * R;
    const deltaLatitude = rad2deg(diameter / R);
    const deltaLongitude = rad2deg(diameter / radiusOnLatitude);

    // beware: this only works correctly for positive coordinates, but since we work in DE/AT, this is fine
    this.topLat = center[1] + deltaLatitude;
    this.bottomLat = center[1] - deltaLatitude;
    this.leftLng = center[0] - deltaLongitude;
    this.rightLng = center[0] + deltaLongitude;
  }

  public isWithin(coordinate: LngLat): boolean {
    return coordinate[0] >= this.leftLng && coordinate[0] <= this.rightLng
      && coordinate[1] >= this.bottomLat && coordinate[1] <= this.topLat;
  }
}


export const getFilteredLocations = (
  query: string,
  distributors: FeatureWithDistance[]
): FeatureWithDistance[] => {
  let resultList: FeatureWithDistance[] = [];
  let queryWordswithoutWhitespace: string[] = [];
  let queryWordsLowerCase: string[] = [];
  let resultIdList: number[] = [];
  let sortedIdList: number[] = [];
  let uniqueDistributors: number[] = [];
  let sortedResultList: FeatureWithDistance[] = [];

  query = query.trim();
  const queryWords = query.split(/(\s+)/);

  if (query === '') {
    return distributors;
  }

  try {
    queryWordswithoutWhitespace = queryWords.filter(
      (word) => !isStringContainSpace(word)
    );
    queryWordsLowerCase = queryWordswithoutWhitespace.map((word) =>
      word.toLowerCase()
    );
  } catch (error) {
    throw new Error(`Could not separate words: ${error}`);
  }

  try {
    queryWordsLowerCase.forEach((word) => {
      const matchedDistributors = distributors.filter((distributor) =>
        distributor[1].properties.info.toLowerCase().includes(word)
      );
      resultList = [...resultList, ...matchedDistributors];
    });
  } catch (error) {
    throw new Error(`Could not filter by words: ${error}`);
  }

  try {
    resultIdList = resultList.map((distributor) => +distributor[1].id);
  } catch (error) {
    throw new Error(
      `Could not create an ID list from the filtered distributors: ${error}`
    );
  }

  try {
    sortedIdList = frequencySort(resultIdList);
  } catch (error) {
    throw new Error(`Could not sort the ID list by frequency: ${error}`);
  }

  try {
    uniqueDistributors = sortedIdList.filter(
      (element, index) => sortedIdList.indexOf(element) === index
    );
  } catch (error) {
    throw new Error(`Could not remove duplicate list entries: ${error}`);
  }

  try {
    sortedResultList = uniqueDistributors
      .slice(0, 50)
      .map((id) => resultList.find((distributor) => +distributor[1].id === id));
  } catch (error) {
    throw new Error(
      `Could not remove duplicate list entries and limit the number to 50: ${error}`
    );
  }
  return sortedResultList;
};
